import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import AreaImporter from './DataImporter/AreaImporter';
import AssetImporter from './DataImporter/AssetImporter';
import WorkOrderImporter from './DataImporter/WorkOrderImporter';
import PartsImporter from './DataImporter/PartsImporter';
import DepartmentImporter from './DataImporter/DepartmentImporter';
import StockItemsImporter from './DataImporter/StockItemsImporter';
import MeterReadingsImporter from './DataImporter/MeterReadingsImporter';
import FormImporter from './DataImporter/FormImporter';
import EventsImporter from './DataImporter/EventsImporter';
import TaskImporter from './DataImporter/TaskImporter';
import BillOfMaterialsImporter from './DataImporter/BillOfMaterialsImporter';
import handleDownloadTemplateHelper from './DataImporter/handleDownloadTemplateHelper';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';

const pageChecks = {
  showAssets: '/asset-list',
  showStockItems: '/stock-items',
  showAreas: '/areas',
  showParts: '/parts-list',
  showDepartments: '/hierarchy-structure-list',
  showMeterReadings: '/meter-readings',
  showForms: '/forms',
  showWorkOrders: '/work-orders',
  showEvents: '/events',
  showTasks: '/asset-list',
  showBillOfMaterials: '/asset-list',
};

const DataLoadingLayer = () => {
  const userProfile = useUserProfile();

  const [showAreaImporter, setShowAreaImporter] = useState(false);
  const [showAssetImporter, setShowAssetImporter] = useState(false);
  const [showWorkOrderImporter, setShowWorkOrderImporter] = useState(false);
  const [showPartsImporter, setShowPartsImporter] = useState(false);
  const [showDepartmentImporter, setShowDepartmentImporter] = useState(false);
  const [showStockImporter, setShowStockImporter] = useState(false);
  const [showMeterReadingsImporter, setShowMeterReadingsImporter] = useState(false);
  const [showFormImporter, setShowFormImporter] = useState(false);
  const [showEventsImporter, setShowEventsImporter] = useState(false);
  const [showTaskImporter, setShowTaskImporter] = useState(false);
  const [showBillOfMaterialsImporter, setShowBillOfMaterialsImporter] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const pageAccess = useMemo(() => {
    const pages = Array.isArray(userProfile?.pages) ? userProfile.pages : [];

    return Object.fromEntries(
      Object.entries(pageChecks).map(([key, path]) => [key, pages.includes(path)])
    );
  }, [userProfile?.pages]);

  const cards = [
    {
      title: 'Assets',
      icon: 'mage:delivery-truck',
      description: 'Manage equipment and machinery',
      onOpen: () => setShowAssetImporter(true),
      visible: pageAccess.showAssets,
    },
    {
      title: 'Tasks',
      icon: 'mage:clipboard-2',
      description: 'Create planned maintenance tasks in bulk',
      onOpen: () => setShowTaskImporter(true),
      visible: pageAccess.showTasks,
    },
    {
      title: 'Stock Items',
      icon: 'mage:archive',
      description: 'Manage inventory stock items',
      onOpen: () => setShowStockImporter(true),
      visible: pageAccess.showStockItems,
    },
    {
      title: 'Areas',
      icon: 'mage:map-marker',
      description: 'Define physical locations',
      onOpen: () => setShowAreaImporter(true),
      visible: pageAccess.showAreas,
    },
    {
      title: 'Parts',
      icon: 'mage:shopping-cart',
      description: 'Manage spare parts inventory',
      onOpen: () => setShowPartsImporter(true),
      visible: pageAccess.showParts,
    },
    {
      title: 'Departments',
      icon: 'mage:building-a',
      description: 'Manage departments and sites',
      onOpen: () => setShowDepartmentImporter(true),
      visible: pageAccess.showDepartments,
    },
    {
      title: 'Meter Readings',
      icon: 'mage:health-circle',
      description: 'Import historical meter data',
      onOpen: () => setShowMeterReadingsImporter(true),
      visible: pageAccess.showMeterReadings,
    },
    {
      title: 'Forms',
      icon: 'mage:clipboard-2',
      description: 'Import forms and items',
      onOpen: () => setShowFormImporter(true),
      visible: pageAccess.showForms,
    },
    {
      title: 'Work Orders',
      icon: 'mage:checklist-note',
      description: 'Track and complete maintenance tasks',
      onOpen: () => setShowWorkOrderImporter(true),
      visible: pageAccess.showWorkOrders,
    },
    {
      title: 'Events',
      icon: 'mage:calendar',
      description: 'Import asset downtime and operating events',
      onOpen: () => setShowEventsImporter(true),
      visible: pageAccess.showEvents,
    },
    {
      title: 'Bill Of Materials',
      icon: 'mdi:gear-outline',
      description: 'Import bill of materials for tasks',
      onOpen: () => setShowBillOfMaterialsImporter(true),
      visible: pageAccess.showBillOfMaterials,
    },
  ];

  const handleImportComplete = () => {
    setShowAreaImporter(false);
    setShowAssetImporter(false);
    setShowWorkOrderImporter(false);
    setShowPartsImporter(false);
    setShowDepartmentImporter(false);
    setShowStockImporter(false);
    setShowMeterReadingsImporter(false);
    setShowFormImporter(false);
    setShowEventsImporter(false);
    setShowTaskImporter(false);
    setShowBillOfMaterialsImporter(false);
  };

  const visibleCards = cards.filter(({ visible }) => visible);

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-body'>
        <p>
          You can upload data in bulk on this page to help you get started quickly, especially during onboarding or large-scale data migrations.{' '}
          <strong>Download the template</strong> to see the correct file structure.{' '}
          <strong>See the Reference Tables</strong> to view valid values for fields like departments, statuses, or criticality.
        </p>
        <p>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowHelpModal(true);
            }}
            className="text-primary"
          >
            How the Bulk Import Works
          </a>
        </p>
        <div className='row g-3 mt-16'>
          {visibleCards.map(({ title, icon, description, onOpen }) => (
            <div key={title} className="col-md-6 col-xl-3 my-32">
              <div className="card h-100 text-center p-24 d-flex flex-column align-items-center border">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ backgroundColor: '#eee', width: '64px', height: '64px' }}
                >
                  <Icon icon={icon} style={{ fontSize: '28px' }} />
                </div>
                <h6 className="mb-1 small fw-semibold">{title}</h6>
                <p className="text-muted small mb-3">{description}</p>
                <button
                  className="btn btn-primary btn-sm w-75 mb-2"
                  onClick={onOpen}
                >
                  Import Data
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm w-75"
                  onClick={() => handleDownloadTemplateHelper(title)}
                >
                  Download Template
                </button>
              </div>
            </div>
          ))}
        </div>
        {visibleCards.length === 0 && (
          <div className="alert alert-info mt-3" role="alert">
            You do not have access to any bulk importers yet. Please contact your administrator if you need additional permissions.
          </div>
        )}
      </div>

      {showAreaImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Areas</h5>
                <button type="button" className="btn-close" onClick={() => setShowAreaImporter(false)} />
              </div>
              <div className="modal-body">
                <AreaImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showDepartmentImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Departments</h5>
                <button type="button" className="btn-close" onClick={() => setShowDepartmentImporter(false)} />
              </div>
              <div className="modal-body">
                <DepartmentImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showAssetImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Assets</h5>
                <button type="button" className="btn-close" onClick={() => setShowAssetImporter(false)} />
              </div>
              <div className="modal-body">
                <AssetImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showEventsImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Events</h5>
                <button type="button" className="btn-close" onClick={() => setShowEventsImporter(false)} />
              </div>
              <div className="modal-body">
                <EventsImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showBillOfMaterialsImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Bill Of Materials</h5>
                <button type="button" className="btn-close" onClick={() => setShowBillOfMaterialsImporter(false)} />
              </div>
              <div className="modal-body">
                <BillOfMaterialsImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showTaskImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Tasks</h5>
                <button type="button" className="btn-close" onClick={() => setShowTaskImporter(false)} />
              </div>
              <div className="modal-body">
                <TaskImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showWorkOrderImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Work Orders</h5>
                <button type="button" className="btn-close" onClick={() => setShowWorkOrderImporter(false)} />
              </div>
              <div className="modal-body">
                <WorkOrderImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showPartsImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Parts</h5>
                <button type="button" className="btn-close" onClick={() => setShowPartsImporter(false)} />
              </div>
              <div className="modal-body">
                <PartsImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showStockImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Stock Items</h5>
                <button type="button" className="btn-close" onClick={() => setShowStockImporter(false)} />
              </div>
              <div className="modal-body">
                <StockItemsImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showFormImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Forms</h5>
                <button type="button" className="btn-close" onClick={() => setShowFormImporter(false)} />
              </div>
              <div className="modal-body">
                <FormImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showMeterReadingsImporter && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Meter Readings</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMeterReadingsImporter(false)}
                />
              </div>
              <div className="modal-body">
                <MeterReadingsImporter onComplete={handleImportComplete} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showHelpModal && (
        <div className="modal show d-block mx-4" tabIndex="-1">
          <div className="modal-dialog modal-lg ps-24 pe-32" style={{ height: '98%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">How Bulk Import Works</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowHelpModal(false)}
                />
              </div>
              <div className="modal-body">
                <p>
                  Use this page to upload data in bulk and accelerate setup, onboarding, or system migration. Each section includes templates, field definitions, and validation rules to help you format your data correctly.
                </p>

                <div className="mt-12 text-xl fw-bold">How to Use</div>
                <ul>
                  <li>
                    <strong>Download the Import Template</strong>
                    <br />
                    Each import section provides a downloadable Excel or CSV template with the correct column headers. Use it to prepare your data with the expected structure.
                  </li>
                  <li>
                    <strong>Review Required and Optional Fields</strong>
                    <br />
                    Expand the "Required Columns" and "Optional Columns" panels to see what fields must be included and which are optional.
                  </li>
                  <li>
                    <strong>Reference Lookup Values</strong>
                    <br />
                    Fields like departments, asset types, and statuses must match values already in the system. Use the "Reference Tables" in the side menu to find valid options.
                  </li>
                  <li>
                    <strong>Upload Your File</strong>
                    <br />
                    Once your file is ready, drag and drop or browse to upload. The importer will validate your data and show progress as it processes each row.
                  </li>
                </ul>

                <div className="mt-12 text-xl fw-bold">Tips</div>
                <ul>
                  <li>
                    All date fields must be formatted as <strong>DD/MM/YYYY</strong>.
                  </li>
                  <li>Ensure required fields are filled and avoid leaving blank rows.</li>
                  <li>
                    Make sure lookup values (like cost centers or departments) exactly match the system values—including spelling and capitalization.
                  </li>
                  <li>
                    Use the <strong>Reference Tables</strong> in the sidebar to copy valid values for things like Departments, Statuses, Planning Types, etc.
                  </li>
                </ul>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowHelpModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataLoadingLayer;
